/**
 * Shift roles (ADR-0035 Amendment 7, OMON-357): the role a carer plays on a
 * shift. This mirrors architecture/fhir/codesystems/shift-role.json, and
 * tests/shift-roles.test.ts fails if the two drift apart.
 *
 * - A carer's declared role lives on their own `_encounter` as
 *   `participant[].type[].coding[]` in this system.
 * - The default (`primary`) is applied when the role is READ, never written,
 *   so "declared" and "defaulted" stay distinguishable (the office sees which).
 */
export const SHIFT_ROLE_SYSTEM = "https://fhir.sweettreeconnect.com/CodeSystem/shift-role";

export type ShiftRoleCode = "primary" | "secondary" | "relief" | "shadow";

export interface ShiftRole {
  code: ShiftRoleCode;
  display: string;
}

/** Display order: the order carers and planners see them in. */
export const SHIFT_ROLES: readonly ShiftRole[] = [
  { code: "primary", display: "Primary carer" },
  { code: "secondary", display: "Second carer" },
  { code: "relief", display: "Relief carer" },
  { code: "shadow", display: "Shadowing" },
];

export const DEFAULT_SHIFT_ROLE: ShiftRoleCode = "primary";

export const ALL_SHIFT_ROLE_CODES: readonly ShiftRoleCode[] = SHIFT_ROLES.map((r) => r.code);

export function shiftRoleDisplay(code: string | null | undefined): string {
  return SHIFT_ROLES.find((r) => r.code === code)?.display ?? "Primary carer";
}

export interface EncounterRole {
  code: ShiftRoleCode;
  /** false when nothing was declared and the default was applied. */
  declared: boolean;
}

/**
 * The shift role for one participant of an encounter.
 * `participantRef` is the participant's `actor.reference` path (e.g.
 * `_practitioner/abc`). Without it, the first participant's role is used.
 */
export function encounterRole(
  encounter: { participant?: unknown } | null | undefined,
  participantRef?: string,
): EncounterRole {
  const parts = Array.isArray(encounter?.participant) ? encounter!.participant : [];
  for (const p of parts as Array<Record<string, any>>) {
    const actor = p?.actor?.reference;
    const actorPath = typeof actor === "string" ? actor : actor?.path;
    if (participantRef && actorPath && actorPath !== participantRef) continue;
    for (const t of Array.isArray(p?.type) ? p.type : []) {
      for (const c of Array.isArray(t?.coding) ? t.coding : []) {
        if (c?.system === SHIFT_ROLE_SYSTEM && ALL_SHIFT_ROLE_CODES.includes(c.code)) {
          return { code: c.code, declared: true };
        }
      }
    }
  }
  return { code: DEFAULT_SHIFT_ROLE, declared: false };
}

/** The CodeableConcept written to `participant[].type[]` for a role. */
export function shiftRoleConcept(code: ShiftRoleCode) {
  return { coding: [{ system: SHIFT_ROLE_SYSTEM, code, display: shiftRoleDisplay(code) }] };
}

// ── Who owes an item (ADR-0035 am.8, OMON-362) ────────────────────────────
//
// ONE model from librarian to planner to carer: every catalogue item carries a
// default role set (`_shiftRolesDefault`, default none) and may be locked
// (`_rolesLocked`); every plan item (monitored event or goal) carries
// `_shiftRoles`. The legacy `_mandatory` boolean is read as "every role" /
// "no role" so published plans are never rewritten. Truth table shared with
// the Dart and Python mirrors: architecture/fixtures/shift-roles/roles-of.json.

type RolesSource = Record<string, unknown> | null | undefined;

function readRoleSet(raw: unknown): ShiftRoleCode[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return ALL_SHIFT_ROLE_CODES.filter((c) => raw.includes(c));
}

/** The roles a PLAN ITEM says owe it (explicit set, else the legacy flag). */
export function rolesOf(entry: RolesSource): ShiftRoleCode[] {
  return readRoleSet(entry?._shiftRoles)
    ?? (entry?._mandatory === true ? [...ALL_SHIFT_ROLE_CODES] : []);
}

/** A CATALOGUE definition's default role set (explicit, else the legacy flag). */
export function defaultsOf(definition: RolesSource): ShiftRoleCode[] {
  return readRoleSet(definition?._shiftRolesDefault)
    ?? (definition?._mandatory === true ? [...ALL_SHIFT_ROLE_CODES] : []);
}

/** Is this catalogue definition's role set locked by the librarian? */
export function rolesLocked(definition: RolesSource): boolean {
  return definition?._rolesLocked === true;
}

/**
 * The roles that owe a plan item, with the librarian's lock applied: a locked
 * definition's defaults win over whatever the plan item says, so a lock
 * reaches every existing plan without rewriting it.
 */
export function effectiveRoles(entry: RolesSource, definition: RolesSource): ShiftRoleCode[] {
  return definition && rolesLocked(definition) ? defaultsOf(definition) : rolesOf(entry);
}
